export class PouchDoc {
  _id: string;
  _rev?: string;
  _deleted?: boolean;
  db__trans?: string; // this is our addition. remove it?
};

// Extract the originating deviceId from a doc's `db__trans` field, which is
// formatted as `<iso-timestamp>|<deviceId>` by `DatabaseWrapper.getTransactionId`.
// Returns null both when `db__trans` is absent and when it's present but the
// deviceId segment is missing/empty — these two cases are intentionally
// indistinguishable, since the only callers (legacy-doc fallback and
// PouchDB-internal writes) treat them the same way (assume own-device).
export function getTransactionDeviceId(doc: Pick<PouchDoc, 'db__trans'> | null | undefined): string | null {
  const trans = doc?.db__trans;
  if (!trans) return null;
  return trans.split('|')[1] || null;
}

// Extract the write-time milliseconds from a doc's `db__trans` field — the same
// `<iso-timestamp>|<deviceId>` format `DatabaseWrapper.getTransactionId` writes, shared
// with `getTransactionDeviceId` above so the format is parsed in one file only.
// Returns NaN when the field is absent or its timestamp segment does not parse; callers
// (mergeConflictingDocs' recency tie-break, #42) treat NaN as "recency unknown".
export function getTransactionTimeMs(doc: Pick<PouchDoc, 'db__trans'> | null | undefined): number {
  const trans = doc?.db__trans;
  if (typeof trans !== 'string') return NaN;
  return Date.parse(trans.split('|')[0]);
}

// Returns true if a PouchDB/CouchDB error is a 404 "not found" (missing or deleted document).
//
// Checks `err.name`, not `err.reason` or `err.status`. PouchDB's `PouchError` constructor
// sets `name` and `message` but NOT `reason`. `reason` is only added by
// `createError(template, reason)` when the second arg is passed, and several internal
// PouchDB code paths omit it (e.g. `createError(MISSING_DOC)` without a reason in
// fetchRevisionTree, getLocal, getAttachment). `err.name === 'not_found'` is guaranteed
// by both `createError()` (copies from MISSING_DOC template) and
// `generateErrorFromResponse()` (normalizes raw CouchDB HTTP responses).
// See: node_modules/pouchdb-errors/lib/index.es.js
export function isPouchNotFound(err: any): boolean {
  return err?.name === 'not_found';
}

// Returns true if a PouchDB/CouchDB error is a document revision conflict — the write was
// refused because somebody else changed the same document first, so it has to be merged
// rather than written over.
//
// Accepts BOTH `status === 409` and `name === 'conflict'`. Be precise about why, because the
// obvious reason is wrong: it is NOT that one code path reports one field and another reports
// the other. Every conflict PouchDB itself BUILDS carries both. `PouchError` sets `status`,
// `name`, `message` and `error` as own properties; `createError(REV_CONFLICT)` copies every own
// non-function property off that template; `generateErrorFromResponse()` sets `name` and
// `status` together; and our own capacitor-sqlite adapter constructs its conflict the same way
// (`REV_CONFLICT` in `pouchdb-adapter-capacitor-sqlite/adapter.ts`). So for a caught PouchError
// as things stand, either single-field check happens to give the right answer.
//
// What the union is actually for is the places where the two fields DO come apart:
//   - `generateErrorFromResponse()` only sets `name: 'conflict'` when the raw response carries
//     `error: 'conflict'`. A 409 response without that field keeps `status: 409` and gets
//     `name = err.error || 'unknown'`, so a `name`-only check does not see it.
//   - A raw CouchDB HTTP error object that never went through that normalization at all
//     (the `CouchHttpErrorObject` shape in `client/src/services/error-patterns.ts`).
//   - `bulkDocs` result rows, which are plain objects rather than errors and are not built by
//     any of the paths above. They genuinely vary: `client/src/services/restore-retry.ts` reads
//     a THIRD field, `error`, on rows CouchDB returned over HTTP.
// And nothing structural enforces both fields — each adapter constructs its own errors by hand,
// so "both are always set" is a property of today's adapters, not a guarantee. Accepting the
// union means no caller is correct only by accident, and there is one place to change if a
// future adapter reports a conflict some other way.
//
// Shape-tolerant on purpose: it is handed caught `unknown` errors and `bulkDocs` result rows
// alike, so anything that is not an object with one of those two fields simply answers false.
// See: node_modules/pouchdb-errors/lib/index.es.js
export function isPouchConflict(err: any): boolean {
  return err?.status === 409 || err?.name === 'conflict';
}
